/**
 * batch-70 app-dashboard (FO-526, spec 226 §3.B7) - `CHANGELOG.md` lezen.
 *
 * Puur: markdown in, secties uit. Gedeeld door de api (deployregistratie, sync),
 * het sync-/backfillscript en de drift-test op de `Waar:`-conventie.
 *
 * Een sectie is een kop van niveau 2 (`## `) tot de volgende. De kop heeft in de
 * praktijk drie vormen (allemaal in de echte CHANGELOG):
 *
 *   ## 2026-10-23 - Uitrusting: bestelflow vastgelegd (FO-512)
 *   ## Batch-22 / voertuig-verklaring (FO-113): zakelijk/privé-verklaring ...
 *   ## [Unreleased] \u2014 livegang-fixes                     (met een lang streepje)
 *
 * Uit de kop: de datum (of geen), de FO-nummers (een sectie kan er meer hebben:
 * "(FO-509, FO-510, FO-516)", "(FO-506 x FO-505)", "(FO-499 + FO-505)"), een
 * spec-nummer ("(spec 110)"), de batch ("Batch-48 / ...", "(FO-344, batch-68)") en
 * de module ("Uitrusting:", "Batch-48 / offboarding-cockpit"). Alleen de KOP telt
 * voor de FO's: de tekst noemt vaak verwante FO's.
 *
 * Nooit stil weggooien: een kop die de parser niet aankan (een datum die niet
 * bestaat, een lege titel) wordt een sectie met `geparseerd: false`, de reden en
 * de ruwe tekst. Codeblokken (```) tellen niet als kop.
 */
import { kopHash } from './hash'
import { WAAR_REGEL_RE, parseWaarWaarde } from './waar'

export interface ChangelogSectie {
  /** Stabiele sleutel uit de kop (`cl-<16 hex>`); dezelfde kop twee keer krijgt `-2`. */
  sleutel: string
  /** De koptekst zonder `## `. */
  kop: string
  /** Regelnummer (1-based) van de kop in het bestand. */
  regel: number
  /** 'yyyy-MM-dd', of null (een kop zonder datum of met een ongeldige datum). */
  datum: string | null
  unreleased: boolean
  /** De titel zonder datum, module en FO-haakjes. */
  titel: string
  module: string | null
  foNummers: number[]
  specNummers: number[]
  batch: number | null
  /** De tekst onder de kop, zonder de `Waar:`-regel. */
  tekst: string
  /** De tekst onder de kop zoals hij er staat. */
  ruweTekst: string
  waar: string[]
  waarGeen: boolean
  heeftWaarRegel: boolean
  waarOngeldig: string[]
  geparseerd: boolean
  parseFout: string | null
  /** Vingerafdruk van kop en tekst (een bewerkte sectie is te herkennen). */
  inhoudHash: string
}

const KOP2_RE = /^##(?!#)\s*(.*?)\s*#*\s*$/
const FENCE_RE = /^\s*(```|~~~)/
const DATUM_KOP_RE = /^(\d{4})-(\d{2})-(\d{2})\s*(?:[-\u2013\u2014:]\s*)?(.*)$/
const UNRELEASED_RE = /^\[unreleased\]\s*(?:[-\u2013\u2014:]\s*(.*))?$/i
const FO_RE = /\bFO-0*(\d{1,4})(?!\d)/gi
const SPEC_RE = /\bspecs?\s+(\d{1,4})\b/gi
const BATCH_RE = /\bbatch[-\s]?(\d{1,3})(?!\d)/i

/** De kop zoals hij vergeleken wordt: kleine letters, streepjes en spaties gelijkgetrokken. */
export function normaliseerKop(kop: string): string {
  return kop
    .toLowerCase()
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, ' ')
    .trim()
}

/** De stabiele sleutel van een sectie (zonder het volgnummer bij een dubbele kop). */
export function changelogSectieSleutel(kop: string): string {
  return `cl-${kopHash(normaliseerKop(kop))}`
}

function bestaandeDatum(j: string, m: string, d: string): string | null {
  const ymd = `${j}-${m}-${d}`
  const t = new Date(`${ymd}T00:00:00.000Z`)
  return !Number.isNaN(t.getTime()) && t.toISOString().slice(0, 10) === ymd ? ymd : null
}

function uniek(nummers: number[]): number[] {
  return [...new Set(nummers)]
}

export function foNummersUitTekst(tekst: string): number[] {
  return uniek([...tekst.matchAll(FO_RE)].map((m) => Number(m[1])))
}

export function specNummersUitTekst(tekst: string): number[] {
  return uniek([...tekst.matchAll(SPEC_RE)].map((m) => Number(m[1])))
}

export function batchUitTekst(tekst: string): number | null {
  const m = BATCH_RE.exec(tekst)
  return m ? Number(m[1]) : null
}

/** Een haakjesgroep met een FO-, spec- of batchverwijzing ("(FO-512)", "(spec 94)", "(batch-6 cert)"). */
const VERWIJZING_HAAKJES_RE = /\s*\((?=[^()]*(?:\bFO-\d|\bspecs?\s+\d|\bbatch[-\s]?\d))[^()]*\)/gi

function schoonModule(ruw: string): string | null {
  const m = ruw
    .replace(/\([^)]*\)/g, ' ')
    .replace(/^\s*stream\s+/i, '')
    .replace(/\s+/g, ' ')
    .trim()
  if (m === '' || /\bFO-\d/i.test(m) || m.length > 60) return null
  return m
}

/** Module en titel uit het deel na de datum. */
export function moduleEnTitel(rest: string): { module: string | null; titel: string } {
  const t = rest.trim()
  // "Batch-48 / offboarding-cockpit (spec 94): titel"
  const metStream = /^batch[-\s]?\d+[\w-]*\s*\/\s*([^:]+?)\s*:\s*(.+)$/i.exec(t)
  if (metStream) return { module: schoonModule(metStream[1] ?? ''), titel: (metStream[2] ?? '').trim() }
  // "Batch-66: titel", "Batch-12 (facturen): titel"
  const alleenBatch = /^batch[-\s]?\d+[\w-]*\s*(?:\(([^)]*)\))?\s*:\s*(.+)$/i.exec(t)
  if (alleenBatch) {
    const inHaakjes = (alleenBatch[1] ?? '').replace(/\bbatch[-\s]?\d+\b/gi, '').trim()
    return { module: inHaakjes ? schoonModule(inHaakjes) : null, titel: (alleenBatch[2] ?? '').trim() }
  }
  // "Uitrusting: titel" (de module noemt zelf geen FO: "FO-022 follow-up: ..." is een titel)
  const metModule = /^([^:]{2,60}?)\s*:\s+(.+)$/.exec(t)
  if (metModule && !/\bFO-\d/i.test(metModule[1] ?? '')) {
    const module = schoonModule(metModule[1] ?? '')
    if (module) return { module, titel: (metModule[2] ?? '').trim() }
  }
  return { module: null, titel: t }
}

/** De titel zonder de FO-, spec- en batchhaakjes aan het eind. */
export function schoonTitel(titel: string): string {
  const zonder = titel.replace(VERWIJZING_HAAKJES_RE, '').replace(/\s+/g, ' ').trim()
  return zonder === '' ? titel.trim() : zonder
}

interface KopUitkomst {
  datum: string | null
  unreleased: boolean
  titel: string
  module: string | null
  geparseerd: boolean
  parseFout: string | null
}

function parseKop(kop: string): KopUitkomst {
  if (kop.trim() === '') {
    return { datum: null, unreleased: false, titel: '', module: null, geparseerd: false, parseFout: 'Lege kop' }
  }
  const datum = DATUM_KOP_RE.exec(kop)
  if (datum) {
    const ymd = bestaandeDatum(datum[1] ?? '', datum[2] ?? '', datum[3] ?? '')
    const rest = (datum[4] ?? '').trim()
    if (!ymd) {
      return {
        datum: null,
        unreleased: false,
        titel: rest || kop,
        module: null,
        geparseerd: false,
        parseFout: `De datum ${datum[1]}-${datum[2]}-${datum[3]} bestaat niet`,
      }
    }
    if (rest === '') {
      return { datum: ymd, unreleased: false, titel: kop, module: null, geparseerd: false, parseFout: 'Kop zonder titel na de datum' }
    }
    const { module, titel } = moduleEnTitel(rest)
    return { datum: ymd, unreleased: false, titel: schoonTitel(titel), module, geparseerd: true, parseFout: null }
  }
  const unreleased = UNRELEASED_RE.exec(kop)
  if (unreleased) {
    const rest = (unreleased[1] ?? '').trim()
    const { module, titel } = moduleEnTitel(rest || 'Unreleased')
    return { datum: null, unreleased: true, titel: schoonTitel(titel), module, geparseerd: true, parseFout: null }
  }
  // Een kop zonder datum ("Batch-22 / voertuig-verklaring (FO-113): ..."): wel geparseerd, geen datum.
  const { module, titel } = moduleEnTitel(kop)
  return { datum: null, unreleased: false, titel: schoonTitel(titel), module, geparseerd: true, parseFout: null }
}

function zonderLegeRanden(regels: string[]): string[] {
  let begin = 0
  let eind = regels.length
  while (begin < eind && (regels[begin] ?? '').trim() === '') begin += 1
  while (eind > begin && (regels[eind - 1] ?? '').trim() === '') eind -= 1
  return regels.slice(begin, eind)
}

/** Alle secties van een CHANGELOG, in de volgorde van het bestand. */
export function parseChangelog(markdown: string): ChangelogSectie[] {
  const regels = markdown.replace(/\r\n?/g, '\n').split('\n')
  const koppen: Array<{ index: number; kop: string }> = []
  let inFence = false
  for (let i = 0; i < regels.length; i += 1) {
    const r = regels[i] ?? ''
    if (FENCE_RE.test(r)) {
      inFence = !inFence
      continue
    }
    if (inFence) continue
    const m = KOP2_RE.exec(r)
    if (m) koppen.push({ index: i, kop: m[1] ?? '' })
  }

  const gezien = new Map<string, number>()
  return koppen.map((k, n) => {
    const eind = n + 1 < koppen.length ? (koppen[n + 1]?.index ?? regels.length) : regels.length
    const body = zonderLegeRanden(regels.slice(k.index + 1, eind))
    const ruweTekst = body.join('\n')

    // De eerste `Waar:`-regel buiten een codeblok.
    let waarRegel = -1
    let waarWaarde = ''
    let fence = false
    for (let i = 0; i < body.length && waarRegel === -1; i += 1) {
      const r = body[i] ?? ''
      if (FENCE_RE.test(r)) fence = !fence
      if (fence) continue
      const w = WAAR_REGEL_RE.exec(r)
      if (w) {
        waarRegel = i
        waarWaarde = w[1] ?? ''
      }
    }
    const heeftWaarRegel = waarRegel !== -1
    const waar = heeftWaarRegel ? parseWaarWaarde(waarWaarde) : { waar: [], geen: false, ongeldig: [] }
    const tekst = zonderLegeRanden(body.filter((_, i) => i !== waarRegel)).join('\n')

    const basis = changelogSectieSleutel(k.kop)
    const aantal = (gezien.get(basis) ?? 0) + 1
    gezien.set(basis, aantal)
    const uitkomst = parseKop(k.kop)

    return {
      sleutel: aantal === 1 ? basis : `${basis}-${aantal}`,
      kop: k.kop,
      regel: k.index + 1,
      datum: uitkomst.datum,
      unreleased: uitkomst.unreleased,
      titel: uitkomst.titel,
      module: uitkomst.module,
      foNummers: foNummersUitTekst(k.kop),
      specNummers: specNummersUitTekst(k.kop),
      batch: batchUitTekst(k.kop),
      tekst,
      ruweTekst,
      waar: waar.waar,
      waarGeen: waar.geen,
      heeftWaarRegel,
      waarOngeldig: waar.ongeldig,
      geparseerd: uitkomst.geparseerd,
      parseFout: uitkomst.parseFout,
      inhoudHash: kopHash(`${k.kop}\n${ruweTekst}`),
    }
  })
}
